import { cn } from '@client/utils/styling/cn';
import { createLink, type LinkComponent } from '@tanstack/react-router';
import { Tooltip } from 'antd';
import { Eye } from 'lucide-react';
import { forwardRef, useMemo } from 'react';
import { ButtonIcon } from './ButtonIcon';
import type { IconButtonLinkProps, ViewButtonProps } from './types';

const sizeClasses = {
    micro: 'btn-micro',
    small: 'btn-sm',
    middle: 'btn-md',
    large: 'btn-lg',
} as const;

const iconSizes = {
    micro: 12,
    small: 14,
    middle: 16,
    large: 20,
} as const;

/**
 * Standardised view button for table row actions
 *
 * Uses accent colour by default, customisable via color prop.
 *
 * @example
 * // Button mode - for opening drawers/modals
 * <Button.View onClick={() => openDrawer()} />
 *
 * @example
 * // With tooltip and size
 * <Button.View
 *   onClick={handleView}
 *   tooltip="View details"
 *   size="small"
 * />
 *
 * @example
 * // With custom colour
 * <Button.View onClick={handleView} color="blue" />
 */
export function ButtonView({
    onClick,
    disabled,
    tooltip = 'View',
    size = 'middle',
    color = 'accent',
}: ViewButtonProps) {
    return (
        <ButtonIcon icon={<Eye />} color={color} size={size} tooltip={tooltip} disabled={disabled} onClick={onClick} />
    );
}

/**
 * Base anchor component for view links
 * Styled as an accent icon button using ButtonIcon CSS classes
 */
const ButtonViewLinkBase = forwardRef<HTMLAnchorElement, IconButtonLinkProps>(
    ({ color = 'accent', size = 'middle', tooltip, disabled, className, ...props }, ref) => {
        const linkClasses = useMemo(
            () =>
                cn(
                    'btn-variant',
                    'btn-icon-colored',
                    `btn-color-${color}`,
                    sizeClasses[size],
                    disabled && 'pointer-events-none opacity-50',
                    className,
                ),
            [color, size, disabled, className],
        );

        const link = (
            <a ref={ref} aria-disabled={disabled} className={linkClasses} {...props}>
                <span className="btn-icon">
                    <Eye size={iconSizes[size]} />
                </span>
            </a>
        );

        if (tooltip) {
            return <Tooltip title={tooltip}>{link}</Tooltip>;
        }

        return link;
    },
);

ButtonViewLinkBase.displayName = 'ButtonViewLinkBase';

/**
 * Button.View.Link - Type-safe router link styled as a view icon button
 *
 * Uses TanStack Router for type-safe `to`, `params`, and `search` props.
 * Accent colour by default, customisable via color prop.
 *
 * @example
 * // Navigate to a claim details page
 * <Button.View.Link
 *   to="/console/$orgId/claims/$claimId"
 *   params={{ orgId: '1', claimId: '123' }}
 *   tooltip="View claim"
 * />
 *
 * @example
 * // With search params
 * <Button.View.Link
 *   to="/console/$orgId/reports"
 *   params={{ orgId: '1' }}
 *   search={{ filter: 'active' }}
 * />
 */
export const ButtonViewLink: LinkComponent<typeof ButtonViewLinkBase> = createLink(ButtonViewLinkBase);
